/**
 * VIBE_CODING_STANDARD
 * 
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added `black` variant.
 * 
 * -----
 * SECURITY_RISK: LOW - This component is a UI primitive for buttons.
 *                      It does not handle sensitive data or complex logic.
 * INTENT: Provide a customizable button component with various styles and sizes,
 *         adhering to the application's design system.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure accessibility standards are met
 *                        and that new variants or sizes are consistent with existing styles.
 * LLM_CONFIDENCE: 100% - Standard UI component.
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils.ts'; // Adjusted import path

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        black: 'bg-black text-white hover:bg-gray-800', // NEW: Black variant
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  formAction?: any;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };